/**
 * TYPE DEFINITIONS
 *
 * This file defines the "shape" of our data using TypeScript.
 * Think of these as blueprints that tell TypeScript what properties
 * each object should have and what type those properties are.
 *
 * This helps catch errors early - if you try to use a property that
 * doesn't exist, TypeScript will warn you!
 */

import { AppIcons } from '../constants/icons';

// ============================================================================
// SHARED PRIMITIVE UNIONS

/**
 * The 8 supported item types across the app. Kept as a single-source union so
 * new item types only need to be added here (and to any callers that switch on
 * the value). Every collection item, every AggregatedItem, every BrandProduct,
 * every ItemUsed on a Creation references this type.
 */
export type ItemType =
  | 'polish'
  | 'plate'
  | 'transfer'
  | 'embellishment'
  | 'tool'
  | 'powder'
  | 'acrylic'
  | 'cuticle_care';

// ============================================================================
// STRUCTURED TAG SYSTEM - OPTIONS
// ============================================================================
/**
 * These define the allowed values for each tag category.
 * Using TypeScript "const" arrays lets us:
 * 1. Use them as dropdown/checkbox options in the UI
 * 2. Get type-safety so typos are caught at compile time
 */

// Base Type - What the polish fundamentally IS (single-select)
export const BASE_TYPE_OPTIONS = ['creme', 'crelly', 'jelly', 'sheer'] as const;
export type BaseType = typeof BASE_TYPE_OPTIONS[number];

// Finish - How light interacts with the surface (multi-select)
export const FINISH_OPTIONS = [
  'shimmer',
  'metallic',
  'chrome',
  'glossy',
  'matte',
  'satin',
  'frost',
  'iridescent',
  'glass',
  'holographic'
] as const;
export type Finish = typeof FINISH_OPTIONS[number];

// Holo Type - Holographic/rainbow effects (multi-select)
export const HOLO_TYPE_OPTIONS = [
  'linear holo',
  'scattered holo',
  'holo glitter'
] as const;
export type HoloType = typeof HOLO_TYPE_OPTIONS[number];

// Color-Shift - Color changes at different angles (single-select)
export const COLOR_SHIFT_OPTIONS = ['none', 'duochrome', 'multichrome'] as const;
export type ColorShift = typeof COLOR_SHIFT_OPTIONS[number];

// Particles - Things IN the polish (multi-select)
export const PARTICLE_OPTIONS = [
  // Basic glitter
  'glitter',
  // Size-based glitters
  'micro glitter',
  'small glitter',
  'medium glitter',
  'large glitter',
  'multi-sized glitter',
  // Effect-based glitters
  'reflective glitter',
  'holo glitter',
  'iridescent glitter',
  'matte glitter',
  'galaxy glitter',
  'micro holo glitter',
  // Shape-based glitters
  'hex glitter',
  'bar glitter',
  'shard glitter',
  'shape glitter',
  'round glitter',
  // Flakes
  'flakes',
  'micro flakes',
  'holo flakes',
  'micro holo flakes',
  'UCC flakies',
  // Pigments
  'aurora pigment',
  'OGUP'
] as const;
export type Particle = typeof PARTICLE_OPTIONS[number];

// Special Features - The "tricks" (multi-select)
export const SPECIAL_FEATURE_OPTIONS = [
  'magnetic',
  'thermal',
  'solar',
  'glow-in-the-dark',
  'black light reactive',
  'fluid art',
  'watercolor',
  'crackle',
  'textured',
  'scented',
  'spotted',
  'quick-dry',
  'water based'
] as const;
export type SpecialFeature = typeof SPECIAL_FEATURE_OPTIONS[number];

// Color Family - Primary color(s) (multi-select)
export const COLOR_FAMILY_OPTIONS = [
  'red',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'neutral',
  'brown',
  'black',
  'white',
  'gold',
  'silver',
  'copper'
] as const;
export type ColorFamily = typeof COLOR_FAMILY_OPTIONS[number];

// Utility - What can this polish be used for (multi-select)
export const UTILITY_OPTIONS = [
  'topper',
  'undie',
  'base coat',
  'top coat',
  'stamps well'
] as const;
export type Utility = typeof UTILITY_OPTIONS[number];

// Color Character - The vibe/intensity (multi-select)
export const COLOR_CHARACTER_OPTIONS = [
  'bright',
  'neon',
  'pastel',
  'dusty',
  'vampy',
  'glowy',
  'muted',
  'dark',
  'milky',
  'deep'
] as const;
export type ColorCharacter = typeof COLOR_CHARACTER_OPTIONS[number];

export const RELEASE_TYPE_OPTIONS = [
  'core',           // Permanent/core collection
  'collection',     // Named collection release
  'event',          // Event release (PPU, HHC, etc.)
  'box',            // Subscription box
  'exclusive',      // Retailer exclusive
  'charity',        // Charity polish
  'groupCustom',    // Group custom (Facebook groups, Reddit, etc.)
  'convention',     // Convention exclusive
  'collab',         // Collaboration between brands/people
  'unknown'         // Unknown/unspecified
] as const;
export type ReleaseType = typeof RELEASE_TYPE_OPTIONS[number];

// Set Type - For duos, trios, quads, or larger sets
export const SET_TYPE_OPTIONS = ['duo', 'trio', 'quad', 'set'] as const;
export type SetType = typeof SET_TYPE_OPTIONS[number];

export const SEASON_OPTIONS = ['Spring', 'Summer', 'Fall', 'Winter'] as const;
export type Season = typeof SEASON_OPTIONS[number];

// Release month (1-12)
export const MONTH_OPTIONS = [
  { value: 1, label: 'January', season: 'Winter' },
  { value: 2, label: 'February', season: 'Winter' },
  { value: 3, label: 'March', season: 'Spring' },
  { value: 4, label: 'April', season: 'Spring' },
  { value: 5, label: 'May', season: 'Spring' },
  { value: 6, label: 'June', season: 'Summer' },
  { value: 7, label: 'July', season: 'Summer' },
  { value: 8, label: 'August', season: 'Summer' },
  { value: 9, label: 'September', season: 'Fall' },
  { value: 10, label: 'October', season: 'Fall' },
  { value: 11, label: 'November', season: 'Fall' },
  { value: 12, label: 'December', season: 'Winter' }
] as const;
export type ReleaseMonth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

// Destash Method
export const DESTASH_METHOD_OPTIONS = [
  'sold', 'traded', 'gifted', 'lost', 'damaged', 'used-up', 'other'
] as const;
export type DestashMethod = typeof DESTASH_METHOD_OPTIONS[number];

// ============================================================================
// STRUCTURED TAGS - COMBINED TYPE
// ============================================================================
/**
 * This groups all the structured tag fields together.
 * Makes it easy to pass around as a single object.
 */
export interface StructuredTags {
  baseType?: BaseType;                 // Single-select
  finish?: Finish[];                   // Multi-select
  holoType?: HoloType[];               // Multi-select
  colorShift?: ColorShift;             // Single-select
  particles?: Particle[];              // Multi-select
  specialFeatures?: SpecialFeature[];  // Multi-select
  colorFamily?: ColorFamily[];         // Multi-select
  utility?: Utility[];                 // Multi-select
  colorCharacter?: ColorCharacter[];   // Multi-select
}

// ============================================================================
// TAG CATEGORY METADATA - FOR UI RENDERING
// ============================================================================
/**
 * This metadata helps us render the accordion UI dynamically.
 * Each category has a label, icon, and whether it's single or multi-select.
 */
export const TAG_CATEGORIES = [
  {
    key: 'baseType',
    label: 'Base Type',
    icon: AppIcons.BaseType,
    options: BASE_TYPE_OPTIONS,
    multiSelect: false,
    description: 'What the polish fundamentally is'
  },
  {
    key: 'finish',
    label: 'Finish',
    icon: AppIcons.Finish,
    options: FINISH_OPTIONS,
    multiSelect: true,
    description: 'How light interacts with the surface'
  },
  {
    key: 'holoType',
    label: 'Holo Type',
    icon: AppIcons.HoloType,
    options: HOLO_TYPE_OPTIONS,
    multiSelect: true,
    description: 'Holographic/rainbow effects'
  },
  {
    key: 'colorShift',
    label: 'Color-Shift',
    icon: AppIcons.ColorShift,
    options: COLOR_SHIFT_OPTIONS,
    multiSelect: false,
    description: 'Color changes at different angles'
  },
  {
    key: 'particles',
    label: 'Particles',
    icon: AppIcons.Particles,
    options: PARTICLE_OPTIONS,
    multiSelect: true,
    description: 'Things IN the polish'
  },
  {
    key: 'specialFeatures',
    label: 'Special Features',
    icon: AppIcons.SpecialFeatures,
    options: SPECIAL_FEATURE_OPTIONS,
    multiSelect: true,
    description: 'Special effects and tricks'
  },
  {
    key: 'colorFamily',
    label: 'Color Family',
    icon: AppIcons.Color,
    options: COLOR_FAMILY_OPTIONS,
    multiSelect: true,
    description: 'Primary color(s)'
  },
  {
    key: 'utility',
    label: 'Utility',
    icon: AppIcons.Utility,
    options: UTILITY_OPTIONS,
    multiSelect: true,
    description: 'What this polish can be used for'
  },
  {
    key: 'colorCharacter',
    label: 'Color Character',
    icon: AppIcons.ColorCharacter,
    options: COLOR_CHARACTER_OPTIONS,
    multiSelect: true,
    description: 'The vibe or intensity'
  }
] as const;

// ============================================================================
// COLLECTION ITEM
// ============================================================================
/**
 * CollectionItem represents a single item in a user's collection.
 * This could be a nail polish, stamping plate, tool, or embellishment.
 * 
 * The "?" after a property name means it's optional (might not always exist)
 */
export interface CollectionItem {
  // Firebase document ID - automatically generated
  id: string;
  
  // Who owns this item (Firebase user ID)
  userId: string;
  
  // What type of item is this?
  itemType: ItemType;

  // ---- Type-Specific Categories ----
  category?: 'Nail Lacquer' | 'Gel Polish' | 'Gel Stamping Polish' | 'Dip Powder' | 'Dip Powders' | 'Stamping Polish' | 'Base Coat' | 'Top Coat' | 'Builder Gel' | 'Poly Gel' | 'Treatment';  // For polish
  transferType?: 'Foil' | 'Sticker' | 'Waterslide Decal' | 'Tattoo' | 'Vinyl';   // For transfers
  embellishmentType?: 'Stone' | 'Stud' | 'Charm' | 'Glitter' | 'Other' | 'Chains' | 'Charms' | 'Crystals' | 'Dried Flowers' | 'Foil' | 'Pearls' | 'Rhinestones' | 'Sequins' | 'Studs' | 'Wire';          // For embellishments

  // ---- Acrylic-Specific Fields ----
  acrylicType?: string;                // Type of acrylic (e.g., "Colored Acrylic", "Glitter Acrylic")
  color?: string;                      // Color name for acrylics/items
  size?: string;                       // Size/amount (e.g., "1oz", "2oz")
  system?: string;                     // Acrylic system/brand line

  // ---- Powder-Specific Fields ----
  powderType?: string;                 // Type of powder (e.g., "Dip", "Acrylic", "Chrome")
  effect?: string;                     // Effect type (e.g., "Holographic", "Magnetic")
  effects?: string[];                  // Multiple effects (array)

  // ---- Tool-Specific Fields ----
  toolType?: string;                   // Type of tool (e.g., "Lamp", "File", "Brush")
  material?: string;                   // Material (e.g., "Metal", "Plastic")

  // ---- Cuticle Care-Specific Fields ----
  cuticleCareType?: string;            // Type (e.g., "Oil", "Cream", "Serum", "Balm", "Remover", "Treatment", "Softener", "Exfoliator")

  // ---- Cross-Type: scent/fragrance ----
  // Applicable to polish (scented polishes), cuticle care, powder, acrylic.
  // Previously cuticle care shoved this into the `color` field; a one-time
  // migration moved legacy values to this field.
  scent?: string;

  // ---- Plate-Specific Fields ----
  plateSize?: 'Regular' | 'Full' | 'Mini' | 'XL' | 'Jumbo' | 'Round' | 'Square' | 'Rectangle' | 'Other';
  numberOfImages?: number;
  imageCount?: number;                 // Alternative to numberOfImages
  imageTags?: string[];                // Tags for plate images
  plateStyle?: string;                 // Style of plate
  plateType?: string;                  // Type of plate (e.g., "Stamping Plate", "Stencil")

  // ---- Polish-Specific Fields ----
  polishType?: string;                 // Type of polish
  line?: string;                       // Product line (e.g., "Insta-Dri", "Ultra Chrome", "Mega Last")
  swatchNumber?: string;               // User's swatch reference number
  opacity?: string;                    // Opacity level
  topperType?: string;                 // Type if it's a topper

  // ---- Additional Visual Info ----
  primaryColor?: string;               // Primary/main color
  shape?: string;                      // Shape (various items)
  exclusiveRetailer?: string;          // Where item is exclusive to

  // ---- Purchase Info ----
  purchaseDate?: Date;                 // When item was purchased
  purchaseSource?: string;             // Where purchased from
  updatedAt?: Date;                    // When item was last updated
  powerSource?: string;                // Power source (for tools)
  
  // ---- Basic Information (from your Google Sheets) ----
  quantity: number;                    // How many of this item you own
  brand: string;                       // e.g., "Alchemy Lacquers"
  brandLower?: string;                 // brand.toLowerCase() — denormalized for case-insensitive search + dupe-finder
  collection?: string;                 // e.g., "Halcyon Days collection"
  event?: string;                      // e.g., "Polish Pick Up – 2022/03"
  name: string;                        // e.g., "Begonya"
  nameLower?: string;                  // name.toLowerCase() — denormalized for case-insensitive search + dupe-finder
  slug?: string;                       // polishSlug(brand, name); maintained by Cloud Function trigger
  
  // ---- Color/Visual Information (mostly for polish) ----
  colorDescription: string;            // Full color description from your sheet


  releaseType?: ReleaseType;
  collectionName?: string;
  eventSource?: string;
  eventName?: string;                   // Event name (e.g., "Polish Pick Up", "Hella Handmade Creations")
  eventTheme?: string;
  releaseYear?: number;
  releaseSeason?: Season;
  releaseMonth?: ReleaseMonth;            // 1-12, more precise than season
  releaseNotes?: string;                // Notes about the release (e.g., "New label", "Tweaked formula")
  
  // Release type-specific fields
  boxName?: string;                     // Subscription box name (e.g., "Polished Gamers Box")
  box?: string;                         // LEGACY: Old box field - migrate to boxName
  boxTheme?: string;                    // Box theme (e.g., "March 2024 - Spring")
  groupName?: string;                   // Group custom group name (e.g., "Reddit Lacqueristas")
  groupTheme?: string;                  // Group custom theme
  exclusiveTo?: string;                 // Exclusive retailer (e.g., "Color4Nails")
  exclusiveTheme?: string;              // Exclusive theme (e.g., "Black Friday 2024")
  charityName?: string;                 // Charity name (e.g., "Autism Speaks")
  charityCause?: string;                // Charity cause (e.g., "Autism Awareness")
  conventionName?: string;              // Convention name (e.g., "Polish Con")
  
  // Re-release tracking
  isRerelease?: boolean;                // Flag: this is a re-release of an earlier polish
  originalReleaseYear?: number;         // When it originally debuted
  originalReleaseSeason?: string;       // Original release season
  
  // Collaboration info (can apply to any release type)
  collabWith?: string;                  // Who the collab is with (e.g., "@gotnail, @varnishedvampire")
  collabTheme?: string;                 // Collab theme (e.g., "Halloween 2024")
  collab?: string;                      // LEGACY: Old single collab field - migrate to collabWith
  
  // ============================================================================
  // SET/DUO SYSTEM - Links polishes that belong together
  // ============================================================================
  // For duos, trios, quads, or larger coordinated sets
  setName?: string;                     // Name of the set (e.g., "Daddy Dean Duo", "Sunset Dreams Trio")
  setType?: SetType;                    // Type: 'duo', 'trio', 'quad', or 'set' (for 5+)
  setPartnerIds?: string[];             // Document paths of partner polishes in this set
  // Example: BCB's polish in "Daddy Dean Duo" would have:
  //   setName: "Daddy Dean Duo"
  //   setType: "duo"
  //   setPartnerIds: ["users/xyz/items/swampys-polish-id"]

  isDestashed?: boolean;
  destashDate?: Date;
  destashMethod?: DestashMethod;
  destashSalePrice?: number;
  destashNotes?: string;
  destashStatus?: 'available' | 'pending' | 'sold';
  
  // ============================================================================
  // NEW: STRUCTURED TAG SYSTEM
  // ============================================================================
  baseType?: BaseType;                 // Single-select: creme, crelly, jelly, sheer
  finish?: Finish[];                   // Multi-select: shimmer, metallic, chrome, etc.
  holoType?: HoloType[];               // Multi-select: linear holo, scattered holo, holo glitter
  colorShift?: ColorShift;             // Single-select: none, duochrome, multichrome
  particles?: Particle[];              // Multi-select: glitter, flakes, OGUP, etc.
  specialFeatures?: SpecialFeature[];  // Multi-select: magnetic, thermal, solar, etc.
  colorFamily?: ColorFamily[];         // Multi-select: red, blue, purple, etc.
  utility?: Utility[];                 // Multi-select: topper, undie, stamps well, etc.
  colorCharacter?: ColorCharacter[];   // Multi-select: bright, neon, pastel, vampy, glowy
  
  // ---- LEGACY TAG FIELDS (kept for migration compatibility) ----
  finishTypes?: string[];              // OLD: Array of mixed tags - will be migrated
  tags?: string[];                     // OLD: Hierarchical tags - will be migrated
  
  // ---- Photo Information ----
  hasSwatched: boolean;                // Do you have a swatch? (y/n from sheet)
  swatchPhotos: string[];              // URLs to swatch photos (for polish - from Google Drive or Firebase)
  bottlePhotos?: string[];             // URLs to bottle photos (for polish)
  macroPhotos?: string[];              // URLs to macro photos (for polish)
  inUsePhotos?: string[];              // Photos of nail art using this item
  photoUrls?: string[];                // URLs to photos (for non-polish items)
  photoUrl?: string;                   // Single photo URL (legacy/convenience)
  photos?: Photo[];

  // ---- USAGE TRACKING ----
  timesUsed?: number;                  // Default: 0
  isFavorite?: boolean;                // Default: false
  lastUsedDate?: Date;                 // Optional
  isRareLE?: boolean;                  // Rare or Limited Edition
  swatched?: boolean;                  // Has this item been swatched?

  // ---- Storage & Inventory ----
  storage?: string;                    // Where it's stored (e.g., "Shelf A, Row 3")
  barcode?: string;                    // UPC/SKU/barcode
  
  // ---- Notes ----
  notes?: string;                      // PRIVATE notes - only you see these
  publicNotes?: string;                // PUBLIC notes - visible to everyone on public profile
  
  // ---- App-Specific Features ----
  dateAdded: Date;                     // When was this added to the collection?
  isForSale: boolean;                  // Is this listed in the marketplace?
  price?: number;                      // If for sale, how much?
  /** Currency the OWNER paid for this item, in ISO code (USD, EUR, RON,
   *  etc). Optional because legacy items pre-date the per-item currency
   *  picker — display falls back to the owner's current profile currency
   *  until the one-shot backfill stamps it. NOT the destash-sale currency;
   *  that stays denormalized from the seller profile on destash write. */
  priceCurrency?: string;
  /** True → hide this item from every public surface (someone else's
   *  view of the owner's collection, brand-page collector counts,
   *  Discover / Color Explorer / Dupe Finder cross-user results, and
   *  community aggregations). Marketplace is DELIBERATELY unaffected —
   *  destash listings remain visible because the seller opted in per
   *  item via `isForDestash`. Absent/false = public (default). Owner
   *  keeps seeing every item in their own views, with a lock badge on
   *  private ones. */
  isPrivate?: boolean;
  condition?: 'New' | 'Used - Light Use' | 'Swatched Only' | 'Used - Moderate';
  
  // ---- Destash System ----
  isForDestash?: boolean;              // Quick toggle - "I want to sell/trade this"
  destashPrice?: number;               // Optional override price for destash
  destashCondition?: 'New' | 'Used - Light Use' | 'Swatched Only' | 'Used - Moderate';
  destashQuantityAvailable?: number;   // How many available for destash
  destashQuantity?: number;            // Quantity for destash (marketplace display)
  isOpenToSwaps?: boolean;             // Willing to trade/swap this item
  /** Currency this listing is priced in — denormalized from the
   *  seller's profile at listing time so Marketplace queries don't
   *  need to join with user docs. Kept in sync via the
   *  syncDestashOnProfileUpdate trigger when the seller updates
   *  their profile. Absent === USD (legacy). `null` is written by
   *  `clearDestashFields()` when a listing is toggled off, so stale
   *  values don't linger — hence `| null` alongside `undefined`. */
  destashCurrency?: string | null;
  /** Countries this seller ships to for THIS listing — same source
   *  as above. If `destashShipsToWorldwide` is true, this array is
   *  ignored. */
  destashShipsToCountries?: string[] | null;
  /** True → seller ships anywhere. Set independently of the
   *  countries array so a toggled-off Worldwide doesn't need to
   *  reconstruct the country list. */
  destashShipsToWorldwide?: boolean | null;

  // ---- Metadata ----
  favorited: boolean;                  // Is this a favorite?
  usedInProjects: string[];            // Array of project IDs where this was used
  onWishlist?: boolean;                // Is this on wishlist?
  isoPriority?: 'iso' | 'diso' | 'unicorn';  // PublicWishlist priority level (ISO/DISO/Unicorn)
  needsInfo?: boolean;                 // Needs more information (for social/suggestions)
  collectionSets?: string[];           // Custom collection groupings (e.g., "My Holos", "Wedding Kit")
  createdAt?: Date;                    // When item was created (for sorting)
  status?: 'available' | 'pending' | 'sold';  // Item availability status

  // ---- Soft Delete ----
  isDeleted?: boolean;                 // Soft delete flag
  deletedAt?: Date;                    // When item was soft deleted

  // ---- Marketplace Copy Tracking ----
  copiedFrom?: {                       // Track where item was copied from (marketplace)
    sellerId: string;
    sellerUsername?: string;
    originalItemId: string;
    copiedAt: string;
  };
}

// ============================================================================
// PROJECT
// ============================================================================
/**
 * Project represents a nail art creation - like a "recipe" that brings
 * together polishes, tools, and inspiration photos.
 */
export interface Project {
  id: string;
  userId: string;
  
  // ---- Basic Info ----
  title: string;                       // e.g., "Galaxy Nails"
  description: string;                 // What's this project about?
  
  // ---- Visual Content ----
  inspirationPhotos: string[];         // URLs to photos of the finished nail art
  
  // ---- Items Used ----
  itemsUsed: string[];                 // Array of CollectionItem IDs used in this project
  
  // ---- Instructions ----
  steps?: string[];                    // Optional step-by-step instructions
  
  // ---- Sharing ----
  isPublic: boolean;                   // Can other users see this?
  
  // ---- Social Features ----
  likes: number;                       // How many likes?
  comments: Comment[];                 // Array of comments (defined below)
  
  // ---- Metadata ----
  dateCreated: Date;
  tags: string[];                      // e.g., ["galaxy", "glitter", "purple"]
}

// ============================================================================
// COMMENT
// ============================================================================
/**
 * Comment on a project
 */
export interface Comment {
  id: string;
  userId: string;                      // Who wrote this comment?
  username: string;                    // Display name of commenter
  text: string;                        // The comment text
  timestamp: Date;                     // When was this posted?
}

// ============================================================================
// MARKETPLACE LISTING
// ============================================================================
/**
 * Listing represents an item for sale/trade in the marketplace
 */
export interface Listing {
  id: string;
  sellerId: string;                    // Who's selling this?
  itemId: string;                      // Which CollectionItem is being sold?
  
  // ---- Sale Details ----
  price: number;
  condition: 'new' | 'swatched-once' | 'used-multiple' | 'vintage';
  shippingInfo: string;                // e.g., "$5 shipping in US"
  
  // ---- Status ----
  status: 'available' | 'pending' | 'sold';
  
  // ---- Interested Buyers ----
  interestedBuyers: string[];          // Array of user IDs who expressed interest
  
  // ---- Metadata ----
  datePosted: Date;
}

// ============================================================================
// USER
// ============================================================================
/**
 * User profile - basic info about a user
 */
export interface User {
  id: string;                          // Firebase auth UID
  email: string;
  username: string;                    // Unique username
  displayName: string;                 // What to show on their profile
  
  // ---- Profile Info ----
  profilePhoto?: string;               // URL to profile photo
  bio?: string;                        // About me text
  location?: string;                   // Free-text display location (e.g., "Rio Rancho, NM")

  // ---- Shipping & Currency (structured, ISO codes) ----
  /** Alpha-2 ISO code of the seller's origin country. Also used as
   *  the default shipping-target when browsing Marketplace as a
   *  buyer. */
  shipsFromCountry?: string;
  /** Alpha-2 ISO codes this seller ships to. Empty array or absent
   *  means "ships only within `shipsFromCountry`". Ignored when
   *  `shipsToWorldwide` is true. */
  shipsToCountries?: string[];
  /** True → seller ships anywhere. */
  shipsToWorldwide?: boolean;
  /** Currency code (USD, EUR, RON, etc.) the seller accepts for all
   *  their listings. See src/constants/currencies.ts. */
  currency?: string;
  /** ISO codes the user has pinned as favorites in the per-item currency
   *  picker. Pinned currencies sort to the top of the picker; the rest of
   *  SUPPORTED_CURRENCIES fills in below. Persisted on the user profile
   *  so it syncs across devices. Managed inline via the star icon in each
   *  picker row — no separate Settings screen. */
  pinnedCurrencies?: string[];

  // ---- Social ----
  followers: string[];                 // Array of user IDs following this user
  following: string[];                 // Array of user IDs this user follows
  
  // ---- Stats ----
  stats: {
    totalItems: number;                // How many items in collection?
    totalProjects: number;             // How many projects created?
    totalSwatches: number;             // How many swatches uploaded?
  };
  
  // ---- Metadata ----
  joinDate: Date;

  // ---- Moderation ----
  /** Admin-set moderation state. Absent === 'active' (normal user).
   *  'suspended' → temporary write timeout; UI stays accessible but reads-only
   *  and shows a non-dismissible banner.
   *  'banned' → account terminated; mobile app renders a full-screen "banned"
   *  gate instead of the main tabs. Enforced by Firestore rules on any
   *  write path a user could use to spread abuse. */
  moderationState?: 'active' | 'suspended' | 'banned';
}

// ============================================================================
// FORM DATA TYPES
// ============================================================================
/**
 * These are simplified versions used for forms before saving to the database
 */

// When adding a new item via form
export interface NewItemFormData {
  itemType: ItemType;
  brand: string;
  name: string;
  quantity: number;
  colorDescription?: string;
  line?: string;                       // Product line (e.g., "Insta-Dri", "Ultra Chrome")
  swatchNumber?: string;               // User's swatch reference number
  
  // NEW: Structured tags for forms
  baseType?: BaseType;
  finish?: Finish[];
  holoType?: HoloType[];
  colorShift?: ColorShift;
  particles?: Particle[];
  specialFeatures?: SpecialFeature[];
  colorFamily?: ColorFamily[];
  utility?: Utility[];
  colorCharacter?: ColorCharacter[];
  
  notes?: string;
}

// When creating a new project via form
export interface NewProjectFormData {
  title: string;
  description: string;
  isPublic: boolean;
  tags: string[];
}

export interface Photo {
  url: string;
  caption?: string;
  isDefault: boolean;
  creditName?: string;
  creditUrl?: string;
  dmcaStatus?: 'active' | 'flagged' | 'hidden' | 'removed' | 'cleared';
  dmcaClaimId?: string;
  // True when the uploading user explicitly attested (via the rights
  // checkbox) that they have the right to share this image. Legacy photos
  // uploaded before the checkbox shipped are absent/undefined and get
  // grandfathered into their existing visibility (own item card + Photos
  // from Collectors gallery) — but only `photoRightsAttested === true`
  // photos are eligible to represent a polish as a discovery tile cover.
  photoRightsAttested?: boolean;
  /** Optional per-photo context. Populated when the uploader chose to
   *  describe the shot (e.g. "medium warm undertone", "natural
   *  daylight") — helps viewers understand what conditions the photo
   *  represents. Absent by default; existing photos are unaffected. */
  skinTone?: string;
  lighting?: string;
}